import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, Renderer2} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import { BrandConstants, BrandEnum } from '@infrastructure/brand.enum';
import { PriceSettingEnum } from '@infrastructure/price-setting.enum';
import { ModalComponent } from '@modals/modal.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AftersalesService } from '@services/aftersales.service';
import { BagCountService } from '@services/bag-count.service';
import { BrandModeService } from '@services/brandmode.service';
import { GuidService } from '@services/guid.service';
import { ImageService } from '@services/image.service';
import { LoginService } from '@services/login.service';
import { ManualConfigService } from '@services/manual-config.service';
import { PriceSettingsService } from '@services/price-settings.service';
import { ThemeService } from '@services/theme.service';
import { UserService } from '@services/user.service';
import { StatusService } from '@services/v2/status.service';
import { AppModeEnum, AppState } from '@shared/app-state';
import 'lazysizes';
import 'reflect-metadata';
import { debounceTime, Observable, Subscription } from 'rxjs';
import { LanguageService } from '../_services/language.service';
import { AppConstants } from '../app.constants';
import { routerTransition } from '../shared/transitions/router.transition';
import { HeaderComponent } from './header/header';
import { AlertComponent } from './shared/components/alert/alert.component';
import { LoadingOverlayComponent } from './shared/components/loading-overlay/loading-overlay';
import { AppStore } from './shared/stores/app.store';

@Component({
	selector: 'ldcg-app',
	imports: [
		ModalComponent,
		AlertComponent,
		RouterOutlet,
		CommonModule,
		LoadingOverlayComponent,
		TranslatePipe,
		HeaderComponent,
	],
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [routerTransition()],
})
export class AppComponent implements OnInit {

	// #############################################################################
	// NEW STUFF
	// #############################################################################

	appStore = inject(AppStore);
	themeService = inject(ThemeService);
	ref = inject(ElementRef);


	// #############################################################################

	public showKEYELoginInfo = false;
	public hideLoginInfo = false;
	public showAnimation = false;
	public isReady: boolean = false;
	public currentYear = new Date().getFullYear();
	public preciousModeChangedObservable: Observable<BrandEnum>;
	public preciousModeChangedSubscription: Subscription;
	public isTitanium: boolean;
	public orderUuid: string;
	public userUuid: string;

	private loginRegisterCheckInterval: number = 24 * 60 * 60 * 1000; // 24 hours in miliseconds

	curMode: AppModeEnum;

	constructor(
		private router: Router,
		private languageService: LanguageService,
		private renderer: Renderer2,
		public appState: AppState,
		public imageService: ImageService, // temp hack, for initializing imageurls early
		public manualConfigService: ManualConfigService,
		private userService: UserService,
		private versionService: StatusService,
		private loginService: LoginService,
		private brandModeService: BrandModeService,
		private priceSettingsService: PriceSettingsService,
		private guidService: GuidService,
		private AftersalesService: AftersalesService,
		bagCountService: BagCountService,
	) {
		// Constructed here so the badge refresh subscriptions are active for the app lifetime.
		void bagCountService;
		this.hasTouch();

		let script = document.createElement('script');
		script.type = 'module';
		script.src = environment.realTimeComponentUrl;
		document.getElementsByTagName('head')[0].appendChild(script);
	}

	async ngOnInit() {
		this.appStore.setRef(this.ref);
		this.themeService.updateTheme('titanium', this.ref);

		const userData = await this.userService.GetUser();

		this.isTitanium = this.brandModeService.isTitanium;

		this.preciousModeChangedObservable = this.brandModeService.brandModeChangedObservable();

		this.preciousModeChangedSubscription = this.preciousModeChangedObservable
			.pipe(debounceTime(100))
			.subscribe((x) => {
				this.isTitanium = this.brandModeService.isTitanium;
			});

		this.appState.showPreciousTheme.next(!this.router.url.startsWith('/bag'));

		if (userData?.preciousModuleEnabled) {
			this.syncBrandMode(location.pathname);
		}
		else if (location.pathname.startsWith('/' + BrandConstants.PRECIOUS)) {
			location.pathname = location.pathname.replace('/' + BrandConstants.PRECIOUS, '/' + BrandConstants.TITANIUM);
		}

		this.router.events.subscribe((val) => {
			if (val instanceof NavigationEnd) {
				this.showKEYELoginInfo = val.url.startsWith('/login') && val.url.indexOf('token=') == -1;

				this.syncBrandMode(val.urlAfterRedirects);
			}
		});

		const searchParams = new URLSearchParams(window.location.search);
		var tokenFromUrl = searchParams.get('access_token');
		var usernameFromUrl = searchParams.get('username');
		if (tokenFromUrl && usernameFromUrl) {
			await this.loginService.SetTokenAndUsername(tokenFromUrl);
		}

		if (this.loginService.AllowUnauthenticatedAccess()) {
			this.userUuid = await this.getUserUuidFromUrl(); //Would have preferred to get this value from params, but ActivatedRouter is not an option because this is AppComponent and subscription to Router.Events is too slow

			if (this.guidService.GUIDisEmpty(this.userUuid)) {
				if (!this.loginService.IsLoggedIn() || this.userUuid !== userData?.Uuid) {
					const res = await this.loginService.postLiteLogin(this.userUuid);

					if (res.access_token) {
						await this.loginService.liteLogin(res.access_token);
					}
				}
			}
		}

		const isNotLoginPage = location.pathname !== '/login';

		await this.initializeLocalization();

		const isIE11 = !!(window as any).MSInputMethodContext && !!(document as any).documentMode;

		if (!isIE11) {
			this.showAnimation = true;
		}
		else {
			this.renderer.addClass(document.body, 'is-ie');
		}

		this.appState.showChineseLicenseId = this.languageService.isChina();

		try {
			// Make sure to register user login every day (logins now last 14 days)
			if (isNotLoginPage && !userData?.IsLiteLogin) {
				if (userData) {
					if (!this.appState.priceModuleEnabled.getValue()) {
						this.appState.priceModuleEnabled.next(userData.priceModuleEnabled && userData.PriceSettingAllowed !== PriceSettingEnum.AllowNone);
					}
				}
			}
		}
		catch (error) {
		}
		finally {
			this.isReady = true;
		}

		const token = await this.loginService.GetToken();
		await this.AftersalesService.CheckAndPerformAftersalesFlow(token);

		this.loginService.IsLoggedInObservable().subscribe(async (l) => {
			if (l) {
				this.versionService.scheduleStatusCheck();
			}
		});
	}

	public goToMyKeringEyeWear() {
		location.href = this.languageService.isChina()
			? 'https://my.keringeyewear.cn'
			: 'https://my.keringeyewear.com';
	}

	public hideLoginInfoOverlay() {
		this.hideLoginInfo = true;
	}

	private async getUserUuidFromUrl() {
		const urlParts = window.location.pathname.substring(1).split('/');

		return urlParts[1];
	}

	private syncBrandMode(url: string) {
		const brandMode = url.split(/[/?#]/)[1];

		if (brandMode === BrandConstants.PRECIOUS) {
			this.brandModeService.toggleBrandMode(BrandEnum.Precious);
			this.themeService.updateTheme('precious', this.ref);
		}
		else if (brandMode === BrandConstants.TITANIUM) {
			this.brandModeService.toggleBrandMode(BrandEnum.Titanium);
			this.themeService.updateTheme('titanium', this.ref);
		}
	}

	private async initializeLocalization() {
		try {
			const defaultLang = AppConstants.languages[0];
			this.languageService.addLanguages(AppConstants.languages);
			this.languageService.setDefaultLang(defaultLang);
			await this.setLanguage();
		}
		catch (error) { }
	}

	private async setLanguage() {
		const language = new URLSearchParams(window.location.search).get('language');
		var avaliableLanguages = await this.languageService.getLanguages();

		if (language && avaliableLanguages.includes(language)) {
			await this.languageService.useLanguage(language);
		}
		else {
			await this.languageService.setSessionOrBrowserLanguage();
		}
	}

	hasTouch() {
		if ('ontouchstart' in document.documentElement) {
			this.renderer.addClass(document.body, 'is-touchdevice');
		}

		if ((window.navigator as any).standalone) {
			this.renderer.addClass(document.body, 'is-standalone');
		}
	}

	routerState(outlet: any) {
		return (outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation']);
	}

}
